import React, { useState } from 'react';
import { object } from 'prop-types';
import { useSelector } from 'react-redux';
import { Button, Heading, Stack, Shelf, Text } from '@chaser/components';
import PageContainer from '../../components/PageContainer/index';
import QuickBooksButton from '../../components/QuickbooksButton/index.jsx';
import usePush from '../../hooks/usePush';
import AccountsIQModal from './components/AccountsIQModal';
import ConnectService from '../../services/connect-service';
import CardSection from '../../components/CardSection';
import IntegrationList from './components/IntegrationList';
import OpenAPIModal from './components/OpenAPIModal/index.jsx';
import Sage200Modal from './components/Sage200Modal/index.jsx';
import { DemoIntegrations, IntegrationDisplayNames, Integrations } from '../../util/constants';
import useModal from '../../hooks/useModal';
import cx from 'classnames';
import style from './components/IntegrationList/Integrations.module.scss';
import useTrialExpiry from '../../hooks/useTrialExpired';
import OdooModal from './components/OdooModal';
import StripeModal from './components/StripeModal';
import SageIntacctModal from './components/SageIntacctModal';
import { pendo } from '../../util/logging';
import useCurrentUserRoles from '../../hooks/useCurrentUserRoles';
import { Redirect } from 'react-router-dom';

const Connect = ({ history }) => {
  const { canCreateOrganisation } = useCurrentUserRoles();

  if (!canCreateOrganisation) {
    return <Redirect to="/" />;
  }

  const showModal = useModal();
  const [accountingSoftware, setAccountingSoftware] = useState(null);
  const isQBO = accountingSoftware === Integrations.QUICKBOOKS;
  const { firstName } = useSelector(state => state.loginReducer);

  const { isExpiredTrial } = useTrialExpiry();

  const { execute, loading } = usePush(ConnectService.connect, {
    errorToast: 'Failed to connect',
  });

  const onClick = async () => {
    pendo.track('integration_connect_clicked', {
      integration: accountingSoftware,
    });

    switch (accountingSoftware) {
      case Integrations.ACCOUNTSIQ:
        showModal(<AccountsIQModal />);
        break;
      case Integrations.CSV:
        history.push('/addorganisation/csvconfiguration');
        break;
      case Integrations.OPENAPI:
        showModal(<OpenAPIModal integrationType={accountingSoftware} />);
        break;
      case Integrations.ZAPIER:
        showModal(<OpenAPIModal integrationType={accountingSoftware} />);
        break;
      case Integrations.SAGE50:
        showModal(<OpenAPIModal integrationType={accountingSoftware} />);
        break;
      case DemoIntegrations.OPENAPI:
        showModal(<OpenAPIModal integrationType={Integrations.OPENAPI} sandbox />);
        break;
      case Integrations.SAGE200:
        showModal(<Sage200Modal />);
        break;
      case Integrations.SAGEINTACCT:
        showModal(<SageIntacctModal />);
        break;
      case Integrations.ODOO:
        showModal(<OdooModal />);
        break;
      case Integrations.STRIPE:
        showModal(<StripeModal />);
        break;
      default: {
        const [err, url] = await execute(accountingSoftware);
        if (err) return;
        window.location = url;
      }
    }
  };

  let buttonLabel = 'Connect';
  if (accountingSoftware) {
    buttonLabel =
      accountingSoftware === DemoIntegrations.OPENAPI
        ? 'Continue with Demo Organisation'
        : `Connect with ${IntegrationDisplayNames[accountingSoftware] || accountingSoftware}`;
  }

  return (
    <PageContainer>
      <Stack gap="large">
        <Heading data-testid="welcome-title" data-trackingid="welcome-title">
          Welcome {firstName}
        </Heading>
        <Stack gap="medium">
          <CardSection justifyContent="center">
            <Stack gap="large">
              <IntegrationList
                onIntegrationSelected={setAccountingSoftware}
                selectedIntegration={accountingSoftware}
              />
              {!isExpiredTrial && (
                <React.Fragment>
                  <hr />
                  <Text as="h3" fontSize="heading.3" fontWeight="bold">
                    Curious to see how it works?
                  </Text>
                  <Text color="Mid Neutral 1">
                    Try Chaser with a demo organization pre-filled with sample data. Experience all
                    the features without sharing your data or connecting your system. Send a test
                    chase to yourself and see Chaser in action!
                  </Text>
                  <Button
                    type="button"
                    intent="chaser"
                    appearance="secondary"
                    data-testid={`${DemoIntegrations.OPENAPI}-add`}
                    data-trackingid={`${DemoIntegrations.OPENAPI}-add`}
                    value={DemoIntegrations.OPENAPI}
                    className={cx(
                      style.button,
                      accountingSoftware === DemoIntegrations.OPENAPI && style.active,
                    )}
                    onClick={() => setAccountingSoftware(DemoIntegrations.OPENAPI)}
                  >
                    <Text fontWeight="bold" fontSize="medium" color="orangeAA">
                      Demo Organisation
                    </Text>
                  </Button>
                </React.Fragment>
              )}
            </Stack>
          </CardSection>
          <Shelf justifyContent="flex-end">
            {isQBO ? (
              <QuickBooksButton
                onClick={onClick}
                data-testid="welcome-connect-button"
                data-trackingid="welcome-connect-button"
              />
            ) : (
              <Button
                data-testid="welcome-connect-button"
                data-trackingid="welcome-connect-button"
                disabled={!accountingSoftware}
                appearance={accountingSoftware ? 'primary' : 'secondary'}
                onClick={onClick}
                loading={loading}
              >
                {buttonLabel}
              </Button>
            )}
          </Shelf>
        </Stack>
      </Stack>
    </PageContainer>
  );
};

Connect.propTypes = {
  history: object.isRequired,
};

export default Connect;
